import { FC, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { TaskCenterOrg } from '@wetags/task-center';
import { useQueryClient } from 'react-query';
import {
  TaskCenterQueries,
  TaskCenterTypes,
  TaskCenterUtils,
  useTaskCenterMutations,
  useUpdateTasksSeenStatusMutation,
} from '@frontend/api-task-center';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { genUUIDV4 } from '@frontend/string';
import { SurfaceHeader } from '@frontend/surface-header';
import { taskCenterTrackingIds, Utils } from '@frontend/task-center-components';
import {
  CitationMetadata,
  taskEvents,
  useDeleteTask,
  UseDeleteTaskProps,
  useTaskCenterStore,
  useUnsavedTaskModal,
  useUnsavedTaskModalProps,
} from '@frontend/task-center-scope';
import { Button, ButtonBar, Text, useAlert } from '@frontend/design-system';
import { TabsController } from '../../constants';
import { useAssigneeByLocation, useGetFormDetails, useRemoveQueryByKey, useTaskFormPatient } from '../../hooks';
import { TaskMutationStrategy } from '../../types';
import { convertDateToUTC } from '../../utils';
import { footerStyle } from './create-task.styles';
import TaskForm from './task-form';
import { withPrefetchTaskById } from './with-prefetch-task-by-id';

const deleteTaskTrackingIds: UseDeleteTaskProps['trackingIds'] = {
  deleteClick: taskCenterTrackingIds.deleteTaskFromDetails,
  deleteConfirmClick: taskCenterTrackingIds.deleteTaskConfirm,
  deleteCancelClick: taskCenterTrackingIds.deleteTaskCancel,
};

const unsavedTaskTrackingIds: useUnsavedTaskModalProps['trackingIds'] = {
  unsavedCancelButton: taskCenterTrackingIds.unsavedCancelButton,
  unsavedConfirmButton: taskCenterTrackingIds.unsavedConfirmButton,
  unsavedBackButton: taskCenterTrackingIds.unsavedBackButton,
};

interface CreateTaskProps {
  selectedTask: TaskCenterTypes.Task | null;
}

const CreateTask: FC<CreateTaskProps> = ({ selectedTask }) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task-center');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    taskMode,
    locationIds,
    locationsData,
    setTab,
    setSelectedTask,
    setTaskMode,
    setTaskId,
    setIsTaskCenterOpen,
    isFromContactPanel,
    setIsFromContactPanel,
    shouldGenerateAiTitle,
    isAIRCreatorEnabled,
    setHighlightedTaskId,
  } = useTaskCenterStore([
    'taskMode',
    'setTaskMode',
    'isFromContactPanel',
    'setIsFromContactPanel',
    'locationIds',
    'locationsData',
    'setSelectedTask',
    'setTab',
    'setTaskId',
    'setIsTaskCenterOpen',
    'shouldGenerateAiTitle',
    'isAIRCreatorEnabled',
    'setHighlightedTaskId',
  ]);

  const isEditing = taskMode === 'edit' && !!selectedTask?.id;

  const { mutate: markTaskAsSeen } = useUpdateTasksSeenStatusMutation();

  //TODO: cleanup feature flag
  // https://linear.app/getweave/issue/BIZX-1787/fe-cleanup-feature-flag-for-task-center-indicator
  const [isUnreadTasksIndicatorEnabled] = TaskCenterOrg.useWetag('unread-tasks-indicator');

  const taskId = selectedTask?.id;
  const taskLocationId = selectedTask?.locationId;
  const isTaskUnread = selectedTask?.isUnreadByAssignee;
  const assignee = selectedTask?.assignee;

  useEffect(() => {
    if (!isUnreadTasksIndicatorEnabled) {
      return;
    }

    const currentUserId = getUser()?.userID;
    if (taskMode === 'edit' && taskId && isTaskUnread && taskLocationId && assignee === currentUserId) {
      markTaskAsSeen({ taskIds: [taskId], seenStatus: true, locationId: [taskLocationId] });
    }
  }, [taskMode, markTaskAsSeen, taskId, isTaskUnread, taskLocationId, assignee, isUnreadTasksIndicatorEnabled]);

  const [dueDateHasTime, setDueDateHasTime] = useState(!!selectedTask?.dueDateHasTime);
  const [shakeTitleField, setShakeTitleField] = useState(false);
  const { taskCreateMutation, taskUpdateMutation } = useTaskCenterMutations();
  const { formValues, formProps, haveValuesChanged, getUpdatedValues, syncInitialValues } = useGetFormDetails({
    locationIds,
    selectedTask,
    locationsData,
  });

  function resetCreateEditTaskPanelState() {
    setTaskMode(null);
    setSelectedTask(null);
    setTaskId(null);
    // close task center if the user is coming from contact panel
    if (isFromContactPanel) {
      setIsTaskCenterOpen(false);
      setIsFromContactPanel(false);
    }

    formProps.reset();
  }

  const { ConfirmationModal, modalProps, triggerProps } = useDeleteTask({
    id: selectedTask?.id,
    trackingIds: deleteTaskTrackingIds,
  });
  const { UnsavedTaskConfirmationModal, unsavedTaskModalProps, unsavedTaskTriggerProps, setShouldClosePanel } =
    useUnsavedTaskModal({
      onConfirm: resetCreateEditTaskPanelState,
      trackingIds: unsavedTaskTrackingIds,
      hasProgress: taskMode === 'citation' || haveValuesChanged,
    });

  const { setPatient, patient } = useTaskFormPatient(selectedTask?.patient?.id);
  const { removeQuery } = useRemoveQueryByKey();

  const showButtons = isEditing ? haveValuesChanged : true;

  const trackingIdSet = taskCenterTrackingIds[isEditing ? 'edit' : 'create'];
  // taskMetadata is a dependency and it re-assigns the value on every render, hence memoized it
  const taskMetadata: CitationMetadata = useMemo(
    () => (selectedTask?.metadata ? JSON.parse(selectedTask.metadata) : undefined),
    [selectedTask?.metadata]
  );

  const selectedLocation = formProps.getFieldProps('locationId').value;
  const { data: assignees, isFetching: fetchingPatients } = useAssigneeByLocation(
    [selectedLocation],
    taskMetadata?.source?.locationPhone
  );

  const markTaskAsDone = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const user = getUser();
    if (!selectedTask?.id || !user?.userID) return;
    taskUpdateMutation.mutate(
      { id: selectedTask.id, status: TaskCenterTypes.TaskStatus.STATUS_COMPLETED, updatedBy: user.userID },
      {
        onSuccess: () => {
          alert.success(t('Task updated successfully'));
          queryClient.invalidateQueries(TaskCenterQueries.queryEndpointKeys.tasksList);
          setSelectedTask({ ...selectedTask, status: TaskCenterTypes.TaskStatus.STATUS_COMPLETED });
          const updatedValues = { ...formProps.values, status: TaskCenterTypes.TaskStatus.STATUS_COMPLETED };
          formProps.seedValues(updatedValues);
          syncInitialValues(updatedValues);
        },
        onError: () => {
          alert.error(t('Failed to update task'));
        },
      }
    );
  };

  // @ts-expect-error form values are returned as string instead of task types. Will update with this ticket
  // https://linear.app/getweave/issue/BIZX-721/updated-taskcenter-create-task-form-types
  const getTaskMutation: () => TaskMutationStrategy = () => {
    const user = getUser();
    const taskValues = getUpdatedValues();
    const timezone = Utils.getLocationTimeZone(locationsData, formValues.locationId);

    if (taskMode === 'edit' && selectedTask?.id && user?.userID) {
      const shouldUpdateMetadata =
        isTaskDetailsAIGenerated !== taskMetadata?.isTaskAISuggested &&
        selectedTask.source === TaskCenterTypes.TaskSource.SOURCE_MESSAGING;
      const taskMetaFields = JSON.stringify({ isTaskAISuggested: isTaskDetailsAIGenerated });

      return {
        type: 'update',
        mutation: taskUpdateMutation,
        params: {
          id: selectedTask.id,
          updater: { userId: user.userID },
          ...taskValues,
          ...(patient &&
            taskValues.patient && {
              patient: { id: patient.personId as string, name: getFullName(patient) },
            }),
          ...(taskValues.dueDate && {
            dueDate: convertDateToUTC({ date: taskValues.dueDate, timezone, hasTime: dueDateHasTime }),
          }),
          ...(dueDateHasTime !== selectedTask?.dueDateHasTime && { dueDateHasTime }),
          ...(shouldUpdateMetadata && { taskMetaFields }),
        },
        successText: t('Task updated successfully'),
        errorText: t('Unable to update task at this moment'),
      };
    } else if (taskMode === 'citation') {
      const shouldUpdateMetadata =
        isTaskDetailsAIGenerated !== taskMetadata?.isTaskAISuggested &&
        selectedTask?.source === TaskCenterTypes.TaskSource.SOURCE_MESSAGING;
      if (shouldUpdateMetadata && selectedTask?.metadata) {
        selectedTask.metadata = JSON.stringify({ ...taskMetadata, isTaskAISuggested: isTaskDetailsAIGenerated });
      }

      return {
        type: 'create',
        mutation: taskCreateMutation,
        params: {
          tasks: [
            {
              ...selectedTask,
              ...formValues,
              ...(isAIRCreatorEnabled ? { creator: { userId: user?.userID || '' } } : { created_by: user?.userID }),
              ...(patient && {
                patient: { id: patient.personId as string, name: getFullName(patient) },
              }),
              priority: formValues.priority || TaskCenterTypes.PriorityType.PRIORITY_UNKNOWN,
              ...(taskValues.dueDate && {
                dueDate: convertDateToUTC({ date: taskValues.dueDate, timezone, hasTime: dueDateHasTime }),
              }),
              dueDateHasTime,
            },
          ],
          platform: TaskCenterTypes.Platform.PLATFORM_WEB,
        },
        successText: t('Task created successfully'),
        errorText: t('Unable to create task at this moment'),
      };
    }

    return {
      type: 'create',
      mutation: taskCreateMutation,
      params: {
        tasks: [
          {
            ...taskValues,
            ...(isAIRCreatorEnabled ? { creator: { userId: user?.userID || '' } } : { created_by: user?.userID }),
            ...(patient && {
              patient: { id: patient.personId as string, name: getFullName(patient) },
            }),
            ...(taskValues.dueDate && {
              dueDate: convertDateToUTC({ date: taskValues.dueDate, timezone, hasTime: dueDateHasTime }),
            }),
            source: TaskCenterTypes.TaskSource.SOURCE_TASK_CENTER,
            priority: formValues.priority || TaskCenterTypes.PriorityType.PRIORITY_UNKNOWN,
            dueDateHasTime,
          },
        ],
        platform: TaskCenterTypes.Platform.PLATFORM_WEB,
      },
      successText: t('Task created successfully'),
      errorText: t('Unable to create task at this moment'),
    };
  };

  const saveTask = () => {
    formProps.validate();
    const user = getUser();

    if (!formProps.isComplete) {
      if (!formProps.values.title) {
        timeoutRef.current && clearTimeout(timeoutRef.current);
        setShakeTitleField(true);
        timeoutRef.current = setTimeout(() => setShakeTitleField(false), 400);
      }
      return;
    }

    const config = getTaskMutation();
    const onError = () => {
      alert.error(config.errorText);
    };
    const onSuccess = (data?: { tasks?: TaskCenterTypes.Task[] }) => {
      if (config.type === 'update') {
        taskEvents.emit('TASK_UPDATED', config.params);
      } else if (data?.tasks?.[0]?.id) {
        setHighlightedTaskId(data.tasks[0].id);
      }

      if (formProps.values.assignee === user?.userID) {
        // invalidate widgets cache when there is an action with a task that is assigned to the current user

        // We had to type case the status because it comes as string, but the value is restricted to the TaskStatus
        //  enum values.
        Utils.invalidateWidgetTasksListQuery(queryClient, formProps.values.status as TaskCenterTypes.TaskStatus);
        Utils.invalidateWidgetTasksListQuery(queryClient, selectedTask?.status);
      }

      alert.success(config.successText);
      queryClient.invalidateQueries(TaskCenterQueries.queryEndpointKeys.tasksList);
      setTab(TabsController[formProps.values.status as keyof typeof TabsController]);
      resetCreateEditTaskPanelState();

      if (selectedTask?.id) {
        const { queryKey } = TaskCenterUtils.getTaskByIdQueryData(locationIds, selectedTask.id);
        removeQuery(queryKey);
      }
    };

    if (config.type === 'update') {
      config.mutation.mutate(config.params, { onError, onSuccess });
    } else {
      config.mutation.mutate(config.params, { onError, onSuccess });
    }
  };

  const getMissingFieldsHint = () => {
    const missingTitle = !formValues.title;
    const missingLocation = !formValues.locationId;

    if (!missingTitle && !missingLocation) return null;

    const hintText =
      missingTitle && missingLocation
        ? t('Add Title & Location')
        : missingLocation
        ? t('Add Location')
        : t('Add Title');

    return <Text color='subdued'>{hintText}</Text>;
  };

  useEffect(() => {
    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, []);

  const isCitation = taskMode === 'citation';
  const citation = taskMetadata?.source;

  const { isLoading: isSuggestTaskDetailsLoading, data: aiSuggestedTaskDetails } =
    TaskCenterQueries.useSuggestTaskDetailsWithContext(
      {
        sourceId: selectedTask?.sourceId ?? '',
        createdAt: citation?.created_at ?? '',
        threadId: citation?.thread_id ?? '',
        locationId: selectedTask?.locationId ?? '',
        patientName: selectedTask?.patient?.name,
      },
      {
        enabled:
          shouldGenerateAiTitle &&
          isCitation &&
          !!selectedTask?.sourceId &&
          !!citation?.thread_id &&
          !!citation?.created_at,
      }
    );

  useEffect(() => {
    // Some how the setSelectedTask in this component is getting called multiple times with the same
    // values which is infinite state setting. So I'm adding a check to only update the task when we have
    // new AI suggested details to apply or when the selectedTask changes to prevent unnecessary
    // updates and form seeding.
    if (
      aiSuggestedTaskDetails?.suggestedTitle &&
      selectedTask &&
      taskMetadata &&
      isCitation &&
      (selectedTask.title !== aiSuggestedTaskDetails.suggestedTitle ||
        selectedTask.description !== aiSuggestedTaskDetails.suggestedDescription)
    ) {
      const metadata = JSON.stringify({ ...taskMetadata, isTaskAISuggested: true });

      formProps.seedValues({
        ...formProps.values,
        title: aiSuggestedTaskDetails.suggestedTitle,
        description: aiSuggestedTaskDetails.suggestedDescription,
      });

      setSelectedTask({
        ...selectedTask,
        title: aiSuggestedTaskDetails.suggestedTitle,
        description: aiSuggestedTaskDetails.suggestedDescription,
        metadata: metadata,
        id: genUUIDV4(),
      });
    }
  }, [
    aiSuggestedTaskDetails?.suggestedDescription,
    aiSuggestedTaskDetails?.suggestedTitle,
    formProps,
    selectedTask,
    setSelectedTask,
    taskMetadata,
    isCitation,
  ]);

  const isTaskDetailsAIGenerated = useMemo(() => {
    // The condition checks if the meta data for isTaskAISuggested boolean key(we mark this key as true in when creating
    // a task if the title is generated by AI) is true and also checks if the current input's title and description
    // in the form matches selectedTask's title and description. If both conditions are true then we can be sure that
    // the task details shown to user are AI generated.
    if (
      taskMetadata?.isTaskAISuggested &&
      formProps.values.title === selectedTask?.title &&
      formProps.values.description === selectedTask?.description
    ) {
      return true;
    }
    return false;
  }, [
    formProps.values.description,
    formProps.values.title,
    selectedTask?.description,
    selectedTask?.title,
    taskMetadata?.isTaskAISuggested,
  ]);

  return (
    <>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          {!isFromContactPanel && (
            <SurfaceHeader.Start>
              <SurfaceHeader.Icon
                iconName='caret-left'
                variant='secondary'
                disabled={taskCreateMutation.isLoading}
                trackingId={trackingIdSet.cancelProgressBackButton}
                label={t('Back')}
                {...unsavedTaskTriggerProps}
              />
            </SurfaceHeader.Start>
          )}
          <SurfaceHeader.Title>{isEditing ? t('Task Details') : t('New Task')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            {isEditing && (
              <>
                <SurfaceHeader.Icon
                  iconName='trash'
                  variant='secondary'
                  hoverLabel={t('Delete Task')}
                  {...triggerProps}
                />
                <SurfaceHeader.Divider />
              </>
            )}
            <SurfaceHeader.Icon
              iconName='x'
              label={t('Close')}
              onClick={() => {
                if (haveValuesChanged) {
                  setShouldClosePanel(true);
                }
                unsavedTaskTriggerProps.onClick();
              }}
            />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <TaskForm
        formProps={formProps}
        selectedTask={selectedTask}
        taskMetadata={taskMetadata}
        trackingIdSet={trackingIdSet}
        patient={patient}
        setPatient={setPatient}
        isEditing={isEditing}
        selectedLocation={selectedLocation}
        assignees={assignees}
        fetchingPatients={fetchingPatients}
        formValues={formValues}
        dueDateHasTime={dueDateHasTime}
        setDueDateHasTime={setDueDateHasTime}
        shakeTitleField={shakeTitleField}
        isTaskDetailsAIGenerated={isTaskDetailsAIGenerated}
        isSuggestedTaskDetailsLoading={isSuggestTaskDetailsLoading}
        onMarkAsDone={markTaskAsDone}
      />
      {showButtons && (
        <ButtonBar css={footerStyle}>
          {getMissingFieldsHint()}
          <Button
            variant='secondary'
            trackingId={trackingIdSet.cancelProgressButton}
            label={t('Cancel')}
            {...unsavedTaskTriggerProps}
          >
            {t('Cancel')}
          </Button>
          <Button
            loading={taskCreateMutation.isLoading}
            onClick={saveTask}
            trackingId={trackingIdSet.saveProgressButton}
            label={isEditing ? t('Save') : t('Create Task')}
          >
            {isEditing ? t('Save') : t('Create Task')}
          </Button>
        </ButtonBar>
      )}
      <ConfirmationModal {...modalProps} />
      <UnsavedTaskConfirmationModal {...unsavedTaskModalProps} />
    </>
  );
};

export const CreateOrUpdateTask = withPrefetchTaskById(CreateTask);
